import { sql } from 'drizzle-orm'
import {
  bigint,
  boolean,
  check,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, updatedAt } from './columns.ts'
import { offering } from './offerings.ts'
import { order } from './orders.ts'

/*
 * ── THE DISCOUNT LADDERS USED TO LIVE HERE ───────────────────────────────
 * `discount_ladder`, `discount_ladder_step` and the `discount_ladder_kind`
 * enum: three ladders (VOLUME, RENTAL_DAYS, MULTI_SERVICE) modelled as rows so
 * the panel could retune them without a deploy. The panel never read them and
 * neither did the pricing engine — the block above them even carried an „OPEN
 * DECISION (owner), blocks P5" about what MULTI_SERVICE multiplies, a question
 * about a feature no code was asking.
 *
 * Every ladder that is actually charged is somewhere else now. The volume and
 * rental-day thresholds are the structural rules in `@repo/core/offering`, the
 * amounts an Admin retunes are in Cennik → Reguły (`site_setting`), and
 * `multiServiceScope` — the switch that was to settle that open decision —
 * was itself deleted as dead code.
 *
 * What made this worth removing rather than leaving: the docblock asserted the
 * ladders were live and configurable, so anybody reading the schema to find
 * out how a discount works started in the one place that has never granted
 * one.
 *
 * Dropped on the owner's decision by
 * `20260920140000_martwe_tabele_cms_i_drabinki`, which refuses on a table with
 * rows in it. `discount_code` and `discount_redemption` below are untouched —
 * those are the codes the checkout really honours.
 */

/**
 * What a code takes off: a percentage of the basket, or a flat amount.
 *
 * Two members and no third. A "free shipping" or "one free piece" code would be
 * a rule about WHAT is bought rather than about what it costs, and that belongs
 * in the ladders above — this table answers one question, "how much less".
 */
export const discountCodeKind = pgEnum('discount_code_kind', ['PERCENT', 'AMOUNT'])

/**
 * Codes an Admin mints by hand (spec §14 "Kody rabatowe" · H1).
 *
 * Until now there was no table at all, so the one discount the spec describes
 * as manual was the one the system could not express. Everything here is spec
 * §14 read literally: "Tworzy ręcznie Admin. Procent albo kwota. Działają tylko
 * gdy nie ma innego aktywnego rabatu (wyjątek: kod minus 149 zł za konsultację).
 * Parametry: data ważności, liczba użyć, minimalna wartość koszyka."
 *
 * The exception the spec spells "−149 zł za konsultację" is now −199 zł for a
 * Spotkanie strategiczne, minted when the meeting completes. The quotation is
 * left as the spec wrote it; `consultation.ts` owns the current figures.
 *
 * ── THE EXCLUSIVITY RULE IS THE WHOLE POINT ──────────────────────────────
 * A code does NOT stack. If the basket already earned a volume rung or the
 * combining bonus, the code bounces — and it bounces with a sentence, because
 * a code that silently does nothing is worse than one that is refused. The one
 * exception is spec §8's consultation bonus ("Kod działa nawet jeśli są inne
 * rabaty"), which is why `is_consultation_bonus` is a column and not a magic
 * string somebody has to remember to compare against.
 *
 * ── WHY `used_count` IS A COLUMN AND NOT A COUNT ─────────────────────────
 * Because the limit has to hold under a race. Counting redemptions at
 * validation time is a read-then-write: two checkouts on the last use of a code
 * both count 9 of 10 and both go through. An atomic `UPDATE … SET used_count =
 * used_count + 1 WHERE used_count < max_uses` cannot do that, and it is the
 * only place the limit is real.
 */
export const discountCode = pgTable(
  'discount_code',
  {
    id: primaryId('dcode'),
    /**
     * What the customer types. Stored EXACTLY as the Admin entered it and
     * matched case-insensitively through a functional unique index below —
     * nobody types `WIOSNA24` the way it was written down, and two codes
     * differing only in case would be indistinguishable to the person using
     * them.
     */
    code: text('code').notNull(),

    kind: discountCodeKind('kind').notNull(),
    /**
     * Basis points for `PERCENT`, NET grosze for `AMOUNT`. One column for two
     * units, which is only safe because `kind` is NOT NULL and every reader
     * branches on it before touching this — see `codeDiscountMinor`.
     */
    value: integer('value').notNull(),

    /** Campaign window (spec §14 "Reguły rabatowe mają daty ważności"). */
    validFrom: timestamp('valid_from', { withTimezone: true }),
    validUntil: timestamp('valid_until', { withTimezone: true }),

    /** NULL = unlimited. The atomic claim is what makes a number real. */
    maxUses: integer('max_uses'),
    usedCount: integer('used_count').notNull().default(0),

    /** Spec §14: "minimalna wartość koszyka". NET grosze; NULL = no floor. */
    minBasketNetMinor: bigint('min_basket_net_minor', { mode: 'number' }),

    /**
     * WHICH SERVICE this code is for. NULL = the whole shop.
     *
     * The owner asked for it while looking at the mint form: „dodajmy też listę
     * do wyboru na jaką usługę ten kod rabatowy jest, jeśli na wszystko to
     * zaznaczamy wszystko". Until now a code was a cut off the whole basket and
     * there was no way to say „−15% na montaż" without also discounting the
     * camera hire that happened to be in the same order.
     *
     * NULL rather than a row per service, and the same shape `discount_ladder`
     * already uses for the same question — „NULL = applies to every eligible
     * offering". One nullable column expresses „wszystko" without a join and
     * without a magic row that has to be kept in step with the catalogue.
     *
     * ── WHAT IT SCOPES IS THE BASE, NOT THE ELIGIBILITY ──────────────────
     * A scoped code takes its percentage (or its flat amount) off the lines of
     * THAT offering only — `codeDiscountMinor` is handed the eligible subtotal
     * rather than the basket total. A basket with no such line refuses the code
     * with a sentence, because a code that silently comes off nothing is the
     * worst of the three possible behaviours (see `codes.ts`).
     *
     * `minBasketNetMinor` keeps testing the WHOLE basket. Spec §14 calls it
     * „minimalna wartość koszyka" and that is a different question from what
     * the discount lands on.
     *
     * `cascade`, matching `discount_ladder.offeringId`: a code for a product
     * that has been erased is a code for nothing. Note the interaction with
     * `discount_redemption`'s `restrict` — a SPENT code cannot be deleted, so
     * erasing an offering that has one is refused, which is the right answer
     * for a product with money history behind it.
     */
    offeringId: text('offering_id').references(() => offering.id, { onDelete: 'cascade' }),

    /**
     * The one code that stacks (spec §8's post-consultation −149 zł · I8).
     *
     * A flag rather than a reserved code string, because the rule is about what
     * the code IS, not what it is called: I8 mints one per consultation, each
     * with its own single-use code, and every one of them has to survive a
     * basket that already earned a rung.
     */
    isConsultationBonus: boolean('is_consultation_bonus').notNull().default(false),
    /**
     * Whose code this is. NULL for a campaign code anybody may type.
     *
     * Set for the codes the system mints FOR one person — the amount a
     * Spotkanie strategiczne refunds. Without it the bonus would be a public
     * code with a guessable name: `SPOTKANIE-…` is short by design, because
     * somebody has to read it out, and short plus public is a discount anybody
     * can find.
     *
     * `SET NULL` rather than `CASCADE`: a deleted account must not take a
     * redeemed code's history with it. What is left is a code nobody owns,
     * which `codeProblem` treats as a campaign code — and by then it has been
     * used, so there is nothing left to give away.
     */
    ownerUserId: text('owner_user_id').references(() => user.id, { onDelete: 'set null' }),

    isActive: boolean('is_active').notNull().default(true),
    /** Who minted it. `set null` — a departed Admin must stay deletable. */
    createdByUserId: text('created_by_user_id').references(() => user.id, { onDelete: 'set null' }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    // Case-insensitive uniqueness, matching how the code is looked up. A plain
    // unique index would let `WIOSNA24` and `wiosna24` coexist and then hand
    // the second one to whoever typed either.
    uniqueIndex('discount_code_code_unique').on(sql`lower(${t.code})`),
    index('discount_code_active_idx').on(t.isActive, t.validUntil),

    check('discount_code_value_positive', sql`${t.value} > 0`),
    /*
     * Percent is stored in basis points like every other rate in this schema,
     * so 100% is 10 000 — and the ceiling is 9 900, not 10 000.
     *
     * A code over 100% would pay the customer to order. A code AT 100% does
     * something less obvious and, until this was found, actually happened: it
     * made an order with nothing to pay. The gateway refuses a zero amount, so
     * no payment button appeared, nobody could settle it, and the
     * payment-window job marked it FAILED after seventy-two hours — the
     * performer was never assigned and earned nothing.
     *
     * The same ceiling is applied to the AMOUNT taken off a basket, in
     * `codeDiscountMinor`, because a flat −500 zł code on a 300 zł basket
     * zeroes it just as surely. This CHECK is the half the database can see.
     */
    check('discount_code_percent_range', sql`${t.kind} <> 'PERCENT' OR ${t.value} <= 9900`),
    check('discount_code_uses_nonneg', sql`${t.usedCount} >= 0`),
    check(
      'discount_code_uses_within_max',
      sql`${t.maxUses} IS NULL OR ${t.usedCount} <= ${t.maxUses}`,
    ),
    check(
      'discount_code_window_ordered',
      sql`${t.validFrom} IS NULL OR ${t.validUntil} IS NULL OR ${t.validUntil} > ${t.validFrom}`,
    ),
    check(
      'discount_code_min_basket_nonneg',
      sql`${t.minBasketNetMinor} IS NULL OR ${t.minBasketNetMinor} >= 0`,
    ),
  ],
)

/**
 * One code, on one order (spec §14).
 *
 * Its own table rather than a column on `order`, because the redemption is what
 * makes `used_count` auditable: "this code was used ten times" has to be a list
 * of ten orders, or the counter is a number nobody can check. It is also what
 * makes a code usable once PER CUSTOMER later without a schema change.
 *
 * The amount is FROZEN here. A percentage code re-derived from the order months
 * later would be recomputed against whatever the rate is by then; what was
 * actually taken off is a fact about that moment.
 */
export const discountRedemption = pgTable(
  'discount_redemption',
  {
    id: primaryId('dred'),
    discountCodeId: text('discount_code_id')
      .notNull()
      // `restrict`: a code somebody actually used is part of an order's money
      // trail, and deleting it would leave a discount with no explanation.
      .references(() => discountCode.id, { onDelete: 'restrict' }),
    orderId: text('order_id')
      .notNull()
      .references(() => order.id, { onDelete: 'cascade' }),
    userId: text('user_id').references(() => user.id, { onDelete: 'set null' }),

    /** The code AS TYPED and what it took off, NET grosze. Both frozen. */
    code: text('code').notNull(),
    amountNetMinor: bigint('amount_net_minor', { mode: 'number' }).notNull(),

    createdAt: createdAt(),
  },
  (t) => [
    // One code per order. A basket cannot stack two codes any more than it can
    // stack a code with a ladder — spec §14's rule applied to itself.
    uniqueIndex('discount_redemption_order_unique').on(t.orderId),
    index('discount_redemption_code_idx').on(t.discountCodeId, t.createdAt),
    check('discount_redemption_amount_positive', sql`${t.amountNetMinor} > 0`),
  ],
)

export type DiscountCode = typeof discountCode.$inferSelect
export type NewDiscountCode = typeof discountCode.$inferInsert
export type DiscountRedemption = typeof discountRedemption.$inferSelect
export type NewDiscountRedemption = typeof discountRedemption.$inferInsert
